class FrequentlyBoughtTogether extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.items = [...this.querySelectorAll('[data-fbt-item]')];
    this.total = this.querySelector('[data-fbt-total]');
    this.button = this.querySelector('[data-fbt-add]');
    this.status = this.querySelector('[data-fbt-status]');
    this.currency = this.dataset.currency || 'AUD';
    this.variantMap = this.readCurrentVariants();

    this.onSectionChange = this.onSectionChange.bind(this);
    this.onDocumentChange = this.onDocumentChange.bind(this);
    this.onAdd = this.onAdd.bind(this);

    this.addEventListener('change', this.onSectionChange);
    document.addEventListener('change', this.onDocumentChange);
    this.button?.addEventListener('click', this.onAdd);

    this.syncCurrentProduct();
    this.updateTotal();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onSectionChange);
    document.removeEventListener('change', this.onDocumentChange);
    this.button?.removeEventListener('click', this.onAdd);
  }

  readCurrentVariants() {
    const source = this.querySelector('[data-current-variants]');
    if (!source) return new Map();

    try {
      const variants = JSON.parse(source.textContent);
      return new Map(variants.map((variant) => [String(variant.id), Number(variant.price)]));
    } catch {
      return new Map();
    }
  }

  findMainVariantInput() {
    return [...document.querySelectorAll('form[action*="/cart/add"] [name="id"]')].find(
      (input) => !this.contains(input) && input.value
    );
  }

  syncCurrentProduct() {
    const item = this.querySelector('[data-current-product]');
    const variantInput = this.findMainVariantInput();
    if (!item || !variantInput) return;

    const variantId = String(variantInput.value);
    const price = this.variantMap.get(variantId);
    item.dataset.variantId = variantId;

    if (Number.isFinite(price)) {
      item.dataset.price = String(price);
      const priceOutput = item.querySelector('[data-fbt-price]');
      if (priceOutput) priceOutput.textContent = this.formatMoney(price);
    }
  }

  onSectionChange(event) {
    const select = event.target.closest('[data-fbt-variant-select]');
    if (select) {
      const item = select.closest('[data-fbt-item]');
      const option = select.selectedOptions?.[0] || select;
      item.dataset.variantId = select.value;
      item.dataset.price = option.dataset.price || item.dataset.price;

      const priceOutput = item.querySelector('[data-fbt-price]');
      if (priceOutput) priceOutput.textContent = this.formatMoney(Number(item.dataset.price));
    }

    this.updateTotal();
  }

  onDocumentChange(event) {
    if (this.contains(event.target)) return;
    if (!event.target.closest('form[action*="/cart/add"], variant-picker')) return;

    requestAnimationFrame(() => {
      this.syncCurrentProduct();
      this.updateTotal();
    });
  }

  selectedItems() {
    this.syncCurrentProduct();

    return this.items.filter((item) => item.querySelector('[data-fbt-checkbox]')?.checked);
  }

  updateTotal() {
    const amount = this.selectedItems().reduce((sum, item) => sum + Number(item.dataset.price || 0), 0);
    if (this.total) this.total.textContent = this.formatMoney(amount);
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
      style: 'currency',
      currency: this.currency,
      minimumFractionDigits: 2,
    }).format(cents / 100);
  }

  async onAdd() {
    const selected = this.selectedItems();

    if (!selected.length) {
      this.status.textContent = this.dataset.emptyLabel;
      return;
    }

    const incomplete = selected.find((item) => !item.dataset.variantId);
    if (incomplete) {
      this.status.textContent = this.dataset.requiredLabel;
      incomplete.querySelector('[data-fbt-variant-select]')?.focus();
      return;
    }

    const items = selected.map((item) => ({
      id: Number(item.dataset.variantId),
      quantity: 1,
    }));

    this.button.disabled = true;
    this.button.textContent = this.dataset.addingLabel;
    this.status.textContent = '';

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) throw new Error('Unable to add bundle');
      window.location.assign(this.dataset.cartUrl || `${root}cart`);
    } catch {
      this.status.textContent = this.dataset.errorLabel;
      this.button.disabled = false;
      this.button.textContent = this.dataset.addLabel;
    }
  }
}

if (!customElements.get('frequently-bought-together')) {
  customElements.define('frequently-bought-together', FrequentlyBoughtTogether);
}

